import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import './VendorBillsPage.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

export default function VendorBillsPage() {
  const { currentUser } = useAuth();

  // Tab State: 'upload' | 'gmail' | 'history'
  const [activeTab, setActiveTab] = useState('upload');

  // Vendor Profiles
  const [profiles, setProfiles] = useState([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);

  // Files & Parsed Bills State (Batch Upload)
  const [parsedBills, setParsedBills] = useState([]);
  const [isParsing, setIsParsing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [parseError, setParseError] = useState(null);

  // Gmail Scanner State
  const [gmailBills, setGmailBills] = useState([]);
  const [isScanningGmail, setIsScanningGmail] = useState(false);
  const [gmailScanDays, setGmailScanDays] = useState(30);
  const [gmailCustomQuery, setGmailCustomQuery] = useState('');
  const [gmailScanMessage, setGmailScanMessage] = useState(null);

  // Batch Processing State
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingIndex, setProcessingIndex] = useState(-1);
  const [processProgress, setProcessProgress] = useState({ current: 0, total: 0 });

  // History State
  const [historyLogs, setHistoryLogs] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historySearch, setHistorySearch] = useState('');

  // Modal / Detail State
  const [selectedBillForDetail, setSelectedBillForDetail] = useState(null);

  const fileInputRef = useRef(null);

  // Authorized API Fetch Helper
  const authedFetch = useCallback(async (url, options = {}) => {
    const token = await currentUser.getIdToken();
    const resolvedUrl = url.startsWith('http') ? url : `${API_BASE}${url}`;
    return fetch(resolvedUrl, {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
    });
  }, [currentUser]);

  // Load Vendor Profiles
  const loadProfiles = useCallback(async () => {
    setLoadingProfiles(true);
    try {
      const res = await authedFetch('/api/v1/vendor-bills/profiles');
      const data = await res.json();
      if (data.success && data.profiles) {
        setProfiles(data.profiles);
      }
    } catch (err) {
      console.error('Failed to load vendor profiles:', err);
    } finally {
      setLoadingProfiles(false);
    }
  }, [authedFetch]);

  // Load History Logs
  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await authedFetch('/api/v1/vendor-bills/history');
      const data = await res.json();
      if (data.success && data.logs) {
        setHistoryLogs(data.logs);
      }
    } catch (err) {
      console.error('Failed to load history logs:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, [authedFetch]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
  }, [activeTab, loadHistory]);

  // Scan Gmail for Vendor Invoices
  const handleScanGmail = async () => {
    setIsScanningGmail(true);
    setGmailScanMessage(null);

    try {
      const res = await authedFetch('/api/v1/vendor-bills/gmail/scan', {
        method: 'POST',
        body: JSON.stringify({
          daysBack: gmailScanDays,
          customQuery: gmailCustomQuery,
          maxResults: 30,
        }),
      });

      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to scan Gmail');
      }

      setGmailBills(data.bills || []);
      setGmailScanMessage({
        type: 'success',
        text: `Found and extracted ${data.count || 0} vendor invoice PDFs from Gmail in the last ${gmailScanDays} days.`,
      });
    } catch (err) {
      console.error('Gmail scan error:', err);
      setGmailScanMessage({
        type: 'error',
        text: err.message || 'Error occurred while scanning Gmail.',
      });
    } finally {
      setIsScanningGmail(false);
    }
  };

  // Import Bills from Gmail into Batch Upload Queue
  const importGmailBillToQueue = (bill) => {
    setParsedBills((prev) => {
      // Check if already in queue
      if (prev.some((b) => b.fileName === bill.fileName || (b.invoiceNumber && b.invoiceNumber === bill.invoiceNumber))) {
        alert(`Invoice ${bill.invoiceNumber || bill.fileName} is already in the batch upload queue.`);
        return prev;
      }
      return [...prev, { ...bill, fileId: bill.id || `${bill.fileName}_${Date.now()}` }];
    });
    // Remove from gmail list
    setGmailBills((prev) => prev.filter((b) => b.id !== bill.id));
    setActiveTab('upload');
  };

  const importAllGmailBillsToQueue = () => {
    const readyGmailBills = gmailBills.filter((b) => b.status === 'READY' || b.status === 'UNKNOWN_VENDOR');
    if (readyGmailBills.length === 0) {
      alert('No non-duplicate bills available to import.');
      return;
    }

    setParsedBills((prev) => {
      const existingInvSet = new Set(prev.map((b) => b.invoiceNumber));
      const toAdd = readyGmailBills.filter((b) => !existingInvSet.has(b.invoiceNumber));
      return [...prev, ...toAdd];
    });

    setGmailBills((prev) => prev.filter((b) => !readyGmailBills.some((rgb) => rgb.id === b.id)));
    setActiveTab('upload');
  };

  // Handle File Selection and Parsing
  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    const pdfFiles = Array.from(files).filter(
      (f) => f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf')
    );

    if (pdfFiles.length === 0) {
      alert('Please select valid PDF invoice files.');
      return;
    }

    setIsParsing(true);
    setParseError(null);

    try {
      // Convert all files to base64
      const filePayloads = await Promise.all(
        pdfFiles.map((file) => {
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const base64String = reader.result.split(',')[1];
              resolve({
                id: `${file.name}_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`,
                name: file.name,
                size: file.size,
                base64: base64String,
              });
            };
            reader.onerror = (error) => reject(error);
            reader.readAsDataURL(file);
          });
        })
      );

      const res = await authedFetch('/api/v1/vendor-bills/parse', {
        method: 'POST',
        body: JSON.stringify({ files: filePayloads }),
      });

      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to parse invoices');
      }

      setParsedBills((prev) => [...prev, ...(data.results || [])]);
    } catch (err) {
      console.error('Invoice parsing error:', err);
      setParseError(err.message || 'Error occurred while parsing invoices.');
    } finally {
      setIsParsing(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files) {
      handleFiles(e.dataTransfer.files);
    }
  };

  // Modify / Update a Parsed Bill field
  const updateBillField = (index, field, value, isGmailList = false) => {
    const setter = isGmailList ? setGmailBills : setParsedBills;
    setter((prev) => {
      const updated = [...prev];
      const target = { ...updated[index], [field]: value };

      // Recalculate TDS if subTotal changes or vendor changes
      if (field === 'vendorKey') {
        const prof = profiles.find((p) => p.vendorKey === value);
        if (prof) {
          target.vendorName = prof.vendorName;
          target.vendorId = prof.zohoVendorId;
          target.gstNo = prof.vendorGstin || target.gstNo;
          if (prof.tds?.enabled) {
            target.tdsPercent = prof.tds.tdsPercent;
            target.tdsTaxId = prof.tds.tdsTaxId;
            target.tdsTaxName = prof.tds.tdsTaxName;
            target.tdsAmount = Math.ceil((target.subTotal || 0) * (prof.tds.tdsPercent / 100));
          } else {
            target.tdsPercent = 0;
            target.tdsAmount = 0;
          }
          target.netPayable = parseFloat(((target.grossTotal || 0) - (target.tdsAmount || 0)).toFixed(2));
          target.status = 'READY';
          target.statusMessage = 'Ready for bill creation';
        }
      } else if (field === 'subTotal') {
        const sub = parseFloat(value) || 0;
        target.subTotal = sub;
        if (target.tdsPercent > 0) {
          target.tdsAmount = Math.ceil(sub * (target.tdsPercent / 100));
        }
        target.netPayable = parseFloat(((target.grossTotal || 0) - (target.tdsAmount || 0)).toFixed(2));
      } else if (field === 'grossTotal') {
        const gross = parseFloat(value) || 0;
        target.grossTotal = gross;
        target.netPayable = parseFloat((gross - (target.tdsAmount || 0)).toFixed(2));
      } else if (field === 'tdsAmount') {
        const tds = parseFloat(value) || 0;
        target.tdsAmount = tds;
        target.netPayable = parseFloat(((target.grossTotal || 0) - tds).toFixed(2));
      }

      updated[index] = target;
      return updated;
    });
  };

  const removeBill = (index) => {
    setParsedBills((prev) => prev.filter((_, i) => i !== index));
  };

  const clearAllBills = () => {
    if (window.confirm('Are you sure you want to clear all pending bills?')) {
      setParsedBills([]);
      setParseError(null);
    }
  };

  // Process a single bill in Zoho
  const processSingleBill = async (index, isGmailList = false) => {
    const list = isGmailList ? gmailBills : parsedBills;
    const bill = list[index];
    if (!bill || bill.status === 'PROCESSED') return;

    updateBillField(index, 'status', 'PROCESSING', isGmailList);
    updateBillField(index, 'statusMessage', 'Submitting to Zoho Inventory...', isGmailList);

    try {
      const res = await authedFetch('/api/v1/vendor-bills/process', {
        method: 'POST',
        body: JSON.stringify({
          billData: bill,
          fileBase64: bill.fileBase64,
          fileName: bill.fileName,
        }),
      });

      const data = await res.json();
      if (!data.success) {
        throw new Error(data.error || 'Failed to process bill');
      }

      const setter = isGmailList ? setGmailBills : setParsedBills;
      setter((prev) => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          status: 'PROCESSED',
          statusMessage: 'Successfully created in Zoho',
          createdBillId: data.billId,
          zohoUrl: data.zohoUrl,
        };
        return updated;
      });
    } catch (err) {
      console.error(`Error processing bill ${bill.invoiceNumber}:`, err);
      const setter = isGmailList ? setGmailBills : setParsedBills;
      setter((prev) => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          status: 'ERROR',
          statusMessage: err.message || 'Zoho creation failed',
        };
        return updated;
      });
    }
  };

  // Process all ready bills sequentially
  const processAllReadyBills = async (isGmailList = false) => {
    const list = isGmailList ? gmailBills : parsedBills;
    const readyIndices = list
      .map((b, idx) => (b.status === 'READY' ? idx : -1))
      .filter((idx) => idx !== -1);

    if (readyIndices.length === 0) {
      alert('No bills in READY state to process.');
      return;
    }

    setIsProcessing(true);
    setProcessProgress({ current: 0, total: readyIndices.length });

    for (let i = 0; i < readyIndices.length; i++) {
      const targetIndex = readyIndices[i];
      setProcessingIndex(targetIndex);
      setProcessProgress({ current: i + 1, total: readyIndices.length });
      await processSingleBill(targetIndex, isGmailList);
    }

    setIsProcessing(false);
    setProcessingIndex(-1);
    // Refresh history tab in background
    loadHistory();
  };

  const readyCount = parsedBills.filter((b) => b.status === 'READY').length;
  const processedCount = parsedBills.filter((b) => b.status === 'PROCESSED').length;
  const duplicateCount = parsedBills.filter((b) => b.status === 'DUPLICATE').length;

  const filteredHistory = historyLogs.filter((log) => {
    if (!historySearch) return true;
    const term = historySearch.toLowerCase();
    return (
      (log.billNumber || '').toLowerCase().includes(term) ||
      (log.vendorName || '').toLowerCase().includes(term) ||
      (log.createdBy || '').toLowerCase().includes(term)
    );
  });

  return (
    <div className="vendor-bills-page">
      {/* Top Header */}
      <div className="vb-header">
        <div className="vb-header-title">
          <h1>Vendor Bills Automation Hub</h1>
          <p className="vb-header-subtitle">
            Automated multi-vendor invoice extraction (Gmail intake &amp; manual upload), 2% TDS upper-rounded calculation, multi-GST branch mapping, and 1-click Zoho Inventory bill creation.
          </p>
        </div>

        {/* Tab Switcher */}
        <div className="vb-tab-buttons">
          <button
            className={`vb-tab-btn ${activeTab === 'upload' ? 'active' : ''}`}
            onClick={() => setActiveTab('upload')}
          >
            📄 Batch Upload {parsedBills.length > 0 && `(${parsedBills.length})`}
          </button>
          <button
            className={`vb-tab-btn ${activeTab === 'gmail' ? 'active' : ''}`}
            onClick={() => setActiveTab('gmail')}
          >
            📧 Gmail Auto-Intake {gmailBills.length > 0 && `(${gmailBills.length})`}
          </button>
          <button
            className={`vb-tab-btn ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            📋 Processed Bills Log {historyLogs.length > 0 && `(${historyLogs.length})`}
          </button>
        </div>
      </div>

      {/* Vendor Profiles Summary Bar */}
      <div className="vb-profiles-bar">
        <span className="vb-profiles-label">Configured Vendor Profiles:</span>
        <div className="vb-profiles-tags">
          {loadingProfiles ? (
            <span>Loading vendor profiles...</span>
          ) : (
            profiles.map((p) => (
              <div key={p.vendorKey} className="vb-profile-pill">
                <strong>{p.vendorName}</strong>
                {p.tds?.enabled && <span className="vb-tds-badge">{p.tds.tdsPercent}% TDS (Ceil)</span>}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main Tab 1: Manual Batch Upload & Create */}
      {activeTab === 'upload' && (
        <div className="vb-upload-section">
          {/* Drag & Drop Upload Zone */}
          <div
            className={`vb-dropzone ${isDragging ? 'dragging' : ''} ${isParsing ? 'parsing' : ''}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !isParsing && fileInputRef.current && fileInputRef.current.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              multiple
              accept="application/pdf"
              style={{ display: 'none' }}
              onChange={(e) => handleFiles(e.target.files)}
            />
            {isParsing ? (
              <div className="vb-dropzone-loading">
                <div className="vb-spinner"></div>
                <h3>Extracting &amp; Validating Invoice Data...</h3>
                <p>Analyzing PDF text, detecting GSTINs, calculating TDS, and checking duplicates against Zoho...</p>
              </div>
            ) : (
              <div className="vb-dropzone-content">
                <div className="vb-dropzone-icon">📤</div>
                <h3>Drag and Drop Vendor Invoices (PDFs) here</h3>
                <p>or click to select multiple PDF files at once</p>
                <div className="vb-dropzone-hint">
                  Supports Delhivery (HR/GJ), Shiprocket, Google Ads, Google Cloud, Razorpay, Meta Ads, and Panash
                </div>
              </div>
            )}
          </div>

          {parseError && (
            <div className="vb-alert vb-alert-error">
              <strong>Error:</strong> {parseError}
            </div>
          )}

          {/* Batch Actions & Status Counters */}
          {parsedBills.length > 0 && (
            <div className="vb-batch-toolbar">
              <div className="vb-batch-stats">
                <span className="vb-stat-badge stat-ready">Ready: {readyCount}</span>
                <span className="vb-stat-badge stat-processed">Processed: {processedCount}</span>
                {duplicateCount > 0 && <span className="vb-stat-badge stat-duplicate">Duplicates: {duplicateCount}</span>}
                <span className="vb-stat-total">Total Loaded: {parsedBills.length}</span>
              </div>

              <div className="vb-batch-actions">
                <button
                  className="vb-btn vb-btn-secondary"
                  onClick={clearAllBills}
                  disabled={isProcessing}
                >
                  Clear All
                </button>
                <button
                  className="vb-btn vb-btn-primary"
                  onClick={() => processAllReadyBills(false)}
                  disabled={isProcessing || readyCount === 0}
                >
                  {isProcessing
                    ? `Processing (${processProgress.current}/${processProgress.total})...`
                    : `⚡ Process All Ready Bills (${readyCount})`}
                </button>
              </div>
            </div>
          )}

          {/* Batch Progress Bar */}
          {isProcessing && (
            <div className="vb-progress-container">
              <div className="vb-progress-bar">
                <div
                  className="vb-progress-fill"
                  style={{
                    width: `${(processProgress.current / Math.max(processProgress.total, 1)) * 100}%`,
                  }}
                ></div>
              </div>
              <div className="vb-progress-text">
                Creating Zoho Bills sequentially... ({processProgress.current} of {processProgress.total})
              </div>
            </div>
          )}

          {/* Parsed Bills Table */}
          {parsedBills.length > 0 && (
            <div className="vb-table-card">
              <div className="vb-table-header">
                <h3>Parsed Invoices Preview &amp; Verification</h3>
                <span>Please verify amounts, GST, and TDS deduction before creating bills.</span>
              </div>

              <div className="vb-table-responsive">
                <table className="vb-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>File &amp; Vendor</th>
                      <th>Invoice #</th>
                      <th>Date</th>
                      <th>GSTIN / Supply</th>
                      <th>Taxable Subtotal</th>
                      <th>GST Tax</th>
                      <th>TDS (2% Ceil)</th>
                      <th>Net Payable</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedBills.map((bill, index) => {
                      const isCurrentlyProcessing = processingIndex === index;
                      return (
                        <tr key={bill.fileId || index} className={`vb-row-${bill.status.toLowerCase()}`}>
                          {/* Status Badge */}
                          <td>
                            {bill.status === 'READY' && <span className="vb-badge badge-ready">Ready</span>}
                            {bill.status === 'PROCESSING' && (
                              <span className="vb-badge badge-processing">Processing...</span>
                            )}
                            {bill.status === 'PROCESSED' && (
                              <span className="vb-badge badge-success">✓ Created</span>
                            )}
                            {bill.status === 'DUPLICATE' && (
                              <span className="vb-badge badge-warning" title={bill.statusMessage}>
                                ⚠️ Duplicate
                              </span>
                            )}
                            {bill.status === 'UNKNOWN_VENDOR' && (
                              <span className="vb-badge badge-warning">Select Vendor</span>
                            )}
                            {bill.status === 'ERROR' && (
                              <span className="vb-badge badge-error" title={bill.statusMessage}>
                                Error
                              </span>
                            )}
                          </td>

                          {/* File & Vendor Selection */}
                          <td>
                            <div className="vb-filename" title={bill.fileName}>
                              📄 {bill.fileName}
                            </div>
                            <select
                              className="vb-vendor-select"
                              value={bill.vendorKey || ''}
                              onChange={(e) => updateBillField(index, 'vendorKey', e.target.value, false)}
                              disabled={bill.status === 'PROCESSED' || isCurrentlyProcessing}
                            >
                              <option value="">Select Vendor...</option>
                              {profiles.map((p) => (
                                <option key={p.vendorKey} value={p.vendorKey}>
                                  {p.vendorName}
                                </option>
                              ))}
                            </select>
                          </td>

                          {/* Invoice Number */}
                          <td>
                            <input
                              type="text"
                              className="vb-input-text"
                              value={bill.invoiceNumber || ''}
                              onChange={(e) => updateBillField(index, 'invoiceNumber', e.target.value, false)}
                              placeholder="Invoice #"
                              disabled={bill.status === 'PROCESSED' || isCurrentlyProcessing}
                            />
                            {bill.existingBillId && (
                              <div className="vb-dup-link">
                                <a
                                  href={`https://inventory.zoho.com/app#/bills/${bill.existingBillId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  View Existing Bill ↗
                                </a>
                              </div>
                            )}
                          </td>

                          {/* Date */}
                          <td>
                            <input
                              type="date"
                              className="vb-input-date"
                              value={bill.invoiceDate || ''}
                              onChange={(e) => updateBillField(index, 'invoiceDate', e.target.value, false)}
                              disabled={bill.status === 'PROCESSED' || isCurrentlyProcessing}
                            />
                          </td>

                          {/* GSTIN & State */}
                          <td>
                            <div className="vb-gst-code">{bill.gstNo || 'No GSTIN'}</div>
                            <div className="vb-state-badge">
                              {bill.sourceOfSupply} ➔ {bill.destinationOfSupply}
                            </div>
                          </td>

                          {/* Subtotal */}
                          <td>
                            <input
                              type="number"
                              step="0.01"
                              className="vb-input-number"
                              value={bill.subTotal}
                              onChange={(e) => updateBillField(index, 'subTotal', e.target.value, false)}
                              disabled={bill.status === 'PROCESSED' || isCurrentlyProcessing}
                            />
                          </td>

                          {/* Tax Total */}
                          <td>
                            <span className="vb-tax-val">₹{(bill.taxTotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                          </td>

                          {/* TDS Amount */}
                          <td>
                            <div className="vb-tds-cell">
                              <input
                                type="number"
                                className="vb-input-number"
                                value={bill.tdsAmount}
                                onChange={(e) => updateBillField(index, 'tdsAmount', e.target.value, false)}
                                disabled={bill.status === 'PROCESSED' || isCurrentlyProcessing}
                              />
                              <span className="vb-tds-pill">
                                {bill.tdsPercent > 0 ? `${bill.tdsPercent}% Ceil` : '0%'}
                              </span>
                            </div>
                          </td>

                          {/* Net Payable */}
                          <td>
                            <strong className="vb-net-val">
                              ₹{(bill.netPayable || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </strong>
                          </td>

                          {/* Actions */}
                          <td>
                            <div className="vb-action-btns">
                              {bill.status === 'PROCESSED' ? (
                                <a
                                  href={bill.zohoUrl || `https://inventory.zoho.com/app#/bills/${bill.createdBillId}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="vb-btn vb-btn-success vb-btn-sm"
                                >
                                  Open in Zoho ↗
                                </a>
                              ) : (
                                <button
                                  className="vb-btn vb-btn-primary vb-btn-sm"
                                  onClick={() => processSingleBill(index, false)}
                                  disabled={isProcessing || isCurrentlyProcessing || bill.status === 'DUPLICATE' || !bill.vendorId}
                                >
                                  {isCurrentlyProcessing ? 'Creating...' : 'Create Bill'}
                                </button>
                              )}

                              <button
                                className="vb-btn vb-btn-info vb-btn-sm"
                                onClick={() => setSelectedBillForDetail(bill)}
                                title="View Line Items &amp; Details"
                              >
                                🔍
                              </button>

                              {bill.status !== 'PROCESSED' && (
                                <button
                                  className="vb-btn vb-btn-danger vb-btn-sm"
                                  onClick={() => removeBill(index)}
                                  disabled={isProcessing || isCurrentlyProcessing}
                                  title="Remove"
                                >
                                  ✕
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Main Tab 2: Gmail Auto-Intake */}
      {activeTab === 'gmail' && (
        <div className="vb-gmail-section">
          {/* Controls Bar */}
          <div className="vb-gmail-controls-card">
            <div className="vb-gmail-controls-header">
              <div>
                <h3>📥 Automatic Gmail Invoice Scanner</h3>
                <p>
                  Scans your business mailbox for vendor emails (Delhivery, Google Ads, Meta, Shiprocket, Razorpay, Panash) and extracts invoice PDFs automatically.
                </p>
              </div>
              <button
                className="vb-btn vb-btn-primary vb-btn-lg"
                onClick={handleScanGmail}
                disabled={isScanningGmail}
              >
                {isScanningGmail ? (
                  <>
                    <span className="vb-spinner-sm"></span> Scanning Mailbox...
                  </>
                ) : (
                  '🔍 Scan & Fetch Invoices from Gmail'
                )}
              </button>
            </div>

            <div className="vb-gmail-filters-row">
              <div className="vb-filter-item">
                <label>Timeframe:</label>
                <select
                  value={gmailScanDays}
                  onChange={(e) => setGmailScanDays(Number(e.target.value))}
                  disabled={isScanningGmail}
                >
                  <option value={7}>Last 7 Days</option>
                  <option value={15}>Last 15 Days</option>
                  <option value={30}>Last 30 Days (Default)</option>
                  <option value={60}>Last 60 Days</option>
                  <option value={90}>Last 90 Days</option>
                </select>
              </div>

              <div className="vb-filter-item flex-grow">
                <label>Custom Query Filter (Optional):</label>
                <input
                  type="text"
                  placeholder="e.g. from:delhivery.com OR subject:invoice"
                  value={gmailCustomQuery}
                  onChange={(e) => setGmailCustomQuery(e.target.value)}
                  disabled={isScanningGmail}
                />
              </div>
            </div>
          </div>

          {gmailScanMessage && (
            <div className={`vb-alert vb-alert-${gmailScanMessage.type}`}>
              {gmailScanMessage.text}
            </div>
          )}

          {/* Scanned Bills Table */}
          {gmailBills.length > 0 && (
            <div className="vb-table-card">
              <div className="vb-table-header flex-between">
                <div>
                  <h3>Scanned Gmail Invoices ({gmailBills.length})</h3>
                  <span>Invoices extracted directly from email attachments and pre-checked with Zoho.</span>
                </div>
                <div className="vb-batch-actions">
                  <button
                    className="vb-btn vb-btn-secondary"
                    onClick={importAllGmailBillsToQueue}
                    disabled={isProcessing}
                  >
                    📥 Move All to Batch Upload Queue
                  </button>
                  <button
                    className="vb-btn vb-btn-primary"
                    onClick={() => processAllReadyBills(true)}
                    disabled={isProcessing || gmailBills.filter((b) => b.status === 'READY').length === 0}
                  >
                    ⚡ Process All Ready to Zoho
                  </button>
                </div>
              </div>

              <div className="vb-table-responsive">
                <table className="vb-table">
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Email Sender &amp; Subject</th>
                      <th>Vendor Profile</th>
                      <th>Invoice #</th>
                      <th>Invoice Date</th>
                      <th>Subtotal</th>
                      <th>GST Tax</th>
                      <th>TDS (2% Ceil)</th>
                      <th>Net Balance</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gmailBills.map((bill, index) => (
                      <tr key={bill.id || index} className={`vb-row-${bill.status.toLowerCase()}`}>
                        <td>
                          {bill.status === 'READY' && <span className="vb-badge badge-ready">Ready</span>}
                          {bill.status === 'PROCESSING' && (
                            <span className="vb-badge badge-processing">Processing...</span>
                          )}
                          {bill.status === 'PROCESSED' && (
                            <span className="vb-badge badge-success">✓ Created</span>
                          )}
                          {bill.status === 'DUPLICATE' && (
                            <span className="vb-badge badge-warning" title={bill.statusMessage}>
                              ⚠️ Duplicate
                            </span>
                          )}
                          {bill.status === 'UNKNOWN_VENDOR' && (
                            <span className="vb-badge badge-warning">Select Vendor</span>
                          )}
                          {bill.status === 'ERROR' && (
                            <span className="vb-badge badge-error" title={bill.statusMessage}>
                              Error
                            </span>
                          )}
                        </td>

                        <td>
                          <div className="vb-sender-name">{bill.senderName || bill.senderEmail}</div>
                          <div className="vb-email-subject" title={bill.emailSubject}>
                            📧 {bill.emailSubject}
                          </div>
                          <div className="vb-filename" title={bill.fileName}>
                            📎 {bill.fileName}
                          </div>
                        </td>

                        <td>
                          <select
                            className="vb-vendor-select"
                            value={bill.vendorKey || ''}
                            onChange={(e) => updateBillField(index, 'vendorKey', e.target.value, true)}
                            disabled={bill.status === 'PROCESSED'}
                          >
                            <option value="">Select Vendor...</option>
                            {profiles.map((p) => (
                              <option key={p.vendorKey} value={p.vendorKey}>
                                {p.vendorName}
                              </option>
                            ))}
                          </select>
                        </td>

                        <td>
                          <input
                            type="text"
                            className="vb-input-text"
                            value={bill.invoiceNumber || ''}
                            onChange={(e) => updateBillField(index, 'invoiceNumber', e.target.value, true)}
                            placeholder="Invoice #"
                            disabled={bill.status === 'PROCESSED'}
                          />
                          {bill.existingBillId && (
                            <div className="vb-dup-link">
                              <a
                                href={`https://inventory.zoho.com/app#/bills/${bill.existingBillId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View Zoho Bill ↗
                              </a>
                            </div>
                          )}
                        </td>

                        <td>
                          <input
                            type="date"
                            className="vb-input-date"
                            value={bill.invoiceDate || ''}
                            onChange={(e) => updateBillField(index, 'invoiceDate', e.target.value, true)}
                            disabled={bill.status === 'PROCESSED'}
                          />
                        </td>

                        <td>
                          <input
                            type="number"
                            step="0.01"
                            className="vb-input-number"
                            value={bill.subTotal}
                            onChange={(e) => updateBillField(index, 'subTotal', e.target.value, true)}
                            disabled={bill.status === 'PROCESSED'}
                          />
                        </td>

                        <td>
                          <span className="vb-tax-val">₹{(bill.taxTotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                        </td>

                        <td>
                          <div className="vb-tds-cell">
                            <input
                              type="number"
                              className="vb-input-number"
                              value={bill.tdsAmount}
                              onChange={(e) => updateBillField(index, 'tdsAmount', e.target.value, true)}
                              disabled={bill.status === 'PROCESSED'}
                            />
                            <span className="vb-tds-pill">
                              {bill.tdsPercent > 0 ? `${bill.tdsPercent}% Ceil` : '0%'}
                            </span>
                          </div>
                        </td>

                        <td>
                          <strong className="vb-net-val">
                            ₹{(bill.netPayable || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </strong>
                        </td>

                        <td>
                          <div className="vb-action-btns">
                            {bill.status === 'PROCESSED' ? (
                              <a
                                href={bill.zohoUrl || `https://inventory.zoho.com/app#/bills/${bill.createdBillId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="vb-btn vb-btn-success vb-btn-sm"
                              >
                                Zoho ↗
                              </a>
                            ) : (
                              <>
                                <button
                                  className="vb-btn vb-btn-secondary vb-btn-sm"
                                  onClick={() => importGmailBillToQueue(bill)}
                                  title="Move to Batch Upload Queue"
                                >
                                  📥 Queue
                                </button>
                                <button
                                  className="vb-btn vb-btn-primary vb-btn-sm"
                                  onClick={() => processSingleBill(index, true)}
                                  disabled={bill.status === 'DUPLICATE' || !bill.vendorId}
                                  title="Create directly in Zoho"
                                >
                                  Create
                                </button>
                              </>
                            )}
                            <button
                              className="vb-btn vb-btn-info vb-btn-sm"
                              onClick={() => setSelectedBillForDetail(bill)}
                              title="Inspect Details"
                            >
                              🔍
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Main Tab 3: History Log */}
      {activeTab === 'history' && (
        <div className="vb-history-section">
          <div className="vb-history-toolbar">
            <div className="vb-search-box">
              <input
                type="text"
                placeholder="Search by Bill #, Vendor Name, or User..."
                value={historySearch}
                onChange={(e) => setHistorySearch(e.target.value)}
              />
            </div>
            <button className="vb-btn vb-btn-secondary" onClick={loadHistory} disabled={loadingHistory}>
              {loadingHistory ? 'Refreshing...' : '🔄 Refresh Log'}
            </button>
          </div>

          <div className="vb-table-card">
            <div className="vb-table-header">
              <h3>Zoho Inventory Processed Bills Log</h3>
              <span>All bills generated and synchronized through this portal.</span>
            </div>

            <div className="vb-table-responsive">
              <table className="vb-table">
                <thead>
                  <tr>
                    <th>Created At</th>
                    <th>Bill Number</th>
                    <th>Vendor Name</th>
                    <th>Date</th>
                    <th>Gross Total</th>
                    <th>TDS Deducted</th>
                    <th>Balance / Net</th>
                    <th>PDF Attached</th>
                    <th>Created By</th>
                    <th>Zoho Link</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingHistory ? (
                    <tr>
                      <td colSpan="10" style={{ textAlign: 'center', padding: '2rem' }}>
                        Loading processed bills log...
                      </td>
                    </tr>
                  ) : filteredHistory.length === 0 ? (
                    <tr>
                      <td colSpan="10" style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                        No bills logged yet. Upload or scan invoices to process them.
                      </td>
                    </tr>
                  ) : (
                    filteredHistory.map((log) => (
                      <tr key={log.id || log.billId}>
                        <td>{new Date(log.createdAt).toLocaleString('en-IN')}</td>
                        <td>
                          <strong>{log.billNumber}</strong>
                        </td>
                        <td>{log.vendorName}</td>
                        <td>{log.date}</td>
                        <td>₹{(log.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                        <td>
                          <span className="vb-tds-pill">
                            ₹{(log.tdsAmount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </span>
                        </td>
                        <td>
                          <strong>
                            ₹{(log.balance || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </strong>
                        </td>
                        <td>
                          {log.hasAttachment ? (
                            <span className="vb-badge badge-success">✓ Attached</span>
                          ) : (
                            <span className="vb-badge badge-secondary">—</span>
                          )}
                        </td>
                        <td>
                          <span className="vb-user-pill">{log.createdBy}</span>
                        </td>
                        <td>
                          <a
                            href={log.zohoUrl || `https://inventory.zoho.com/app#/bills/${log.billId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="vb-btn vb-btn-outline vb-btn-sm"
                          >
                            Zoho ↗
                          </a>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Bill Detail / Line Items Modal */}
      {selectedBillForDetail && (
        <div className="vb-modal-overlay" onClick={() => setSelectedBillForDetail(null)}>
          <div className="vb-modal" onClick={(e) => e.stopPropagation()}>
            <div className="vb-modal-header">
              <h2>Invoice Details: {selectedBillForDetail.invoiceNumber || selectedBillForDetail.fileName}</h2>
              <button className="vb-modal-close" onClick={() => setSelectedBillForDetail(null)}>
                ✕
              </button>
            </div>

            <div className="vb-modal-body">
              <div className="vb-modal-grid">
                <div>
                  <span className="vb-modal-label">Vendor:</span>
                  <strong>{selectedBillForDetail.vendorName}</strong>
                </div>
                <div>
                  <span className="vb-modal-label">Invoice Date:</span>
                  <strong>{selectedBillForDetail.invoiceDate}</strong>
                </div>
                <div>
                  <span className="vb-modal-label">GSTIN:</span>
                  <strong>{selectedBillForDetail.gstNo || 'None'}</strong>
                </div>
                <div>
                  <span className="vb-modal-label">TDS Setting:</span>
                  <strong>
                    {selectedBillForDetail.tdsTaxName || '2% TDS Upper Rounded (Ceil)'}
                  </strong>
                </div>
              </div>

              <h3>Line Items</h3>
              <table className="vb-table vb-table-modal">
                <thead>
                  <tr>
                    <th>Item / Description</th>
                    <th>SAC / HSN</th>
                    <th>Rate (₹)</th>
                    <th>Qty</th>
                    <th>Tax ID / Treatment</th>
                  </tr>
                </thead>
                <tbody>
                  {(selectedBillForDetail.lineItems || []).map((li, idx) => (
                    <tr key={idx}>
                      <td>
                        <strong>{li.name}</strong>
                        <div style={{ fontSize: '0.82rem', color: '#666' }}>{li.description}</div>
                      </td>
                      <td>{li.hsn_or_sac || '—'}</td>
                      <td>₹{Number(li.rate).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      <td>{li.quantity}</td>
                      <td>{li.gst_treatment_code || (li.tax_id ? 'Taxable (18%/5%)' : 'Exempt')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="vb-modal-totals">
                <div>Subtotal: ₹{(selectedBillForDetail.subTotal || 0).toFixed(2)}</div>
                <div>Tax Total: ₹{(selectedBillForDetail.taxTotal || 0).toFixed(2)}</div>
                <div>TDS (Ceil): -₹{(selectedBillForDetail.tdsAmount || 0).toFixed(2)}</div>
                <div className="vb-modal-grand">
                  Net Payable: ₹{(selectedBillForDetail.netPayable || 0).toFixed(2)}
                </div>
              </div>
            </div>

            <div className="vb-modal-footer">
              <button className="vb-btn vb-btn-secondary" onClick={() => setSelectedBillForDetail(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
